import React, { useState, useEffect, useContext } from 'react';
import { Card, Select, Divider, message, Spin, Col, Row } from 'antd';
import { BookOutlined } from '@ant-design/icons';
import NhapDiemLopHocPhanPage from './NhapDiemLopHocPhanPage';
import { getAuthHeaders } from '../utils/auth';
import { buildAPIURL } from '../config/apiConfig';
import { AuthContext } from '../contexts/AuthContext';

const { Option } = Select;

interface KhoaHoc {
  KhoaHocID: string;
  TenKhoaHoc: string;
}

interface MonHoc {
  MonHocID: string;
  TenMonHoc: string;
  KhoaHocID: string;
}

interface LopHocPhan {
  LopHocPhanID?: string;
  MaLHP?: string;
  TenLopHP?: string;
  TenLHP?: string;
  TenLopHocPhan?: string;
  TenLop?: string;
  TenMonHoc?: string;
  TenKhoaHoc?: string;
  TenGV?: string;
  SoSinhVien?: number;
}

const GradeInputPage: React.FC = () => {
  const { user } = useContext(AuthContext) || {};
  // Ưu tiên lấy từ user context (FullName backend trả về), fallback fullName/username
  const currentTeacherName = (user as any)?.FullName || (user as any)?.fullName || (user as any)?.username || 'Giáo viên';

  const [khoaHocList, setKhoaHocList] = useState<KhoaHoc[]>([]);
  const [monHocList, setMonHocList] = useState<MonHoc[]>([]);
  const [lopHocPhanList, setLopHocPhanList] = useState<LopHocPhan[]>([]);

  const [selectedKhoaHoc, setSelectedKhoaHoc] = useState<string>('');
  const [selectedMonHoc, setSelectedMonHoc] = useState<string>('');
  const [selectedLopHocPhan, setSelectedLopHocPhan] = useState<string>('');

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchKhoaHoc();
  }, []);

  const fetchKhoaHoc = async () => {
    setLoading(true);
    try {
      const headers = getAuthHeaders();
      console.log('[DEBUG] Headers for /courses:', headers);

      const res = await fetch(buildAPIURL('/courses'), { headers });
      const data = await res.json();
      console.log('[DEBUG] API /courses response:', data);

      // Kiểm tra nếu response có format {success: true, data: [...]}
      const coursesArray = data.success && Array.isArray(data.data) ? data.data : (Array.isArray(data) ? data : []);
      console.log('[DEBUG] Courses array to set:', coursesArray);
      setKhoaHocList(coursesArray);
    } catch (err) {
      console.error('[DEBUG] Lỗi fetch /courses:', err);
      message.error('Không thể tải danh sách khóa học');
      setKhoaHocList([]);
    }
    setLoading(false);
  };

  const fetchMonHoc = async (khoaHocId: string) => {
    if (!khoaHocId) return;
    setLoading(true);
    try {
      const headers = getAuthHeaders();
      console.log('[DEBUG] Headers for /subjects:', headers);

      const res = await fetch(buildAPIURL(`/subjects/by-khoahoc/${encodeURIComponent(khoaHocId)}`), { headers });
      const data = await res.json();
      console.log('[DEBUG] API /subjects response:', data);

      // Kiểm tra nếu response có format {success: true, data: [...]} hoặc {data: [...]}
      const subjectsArray = data.success && Array.isArray(data.data) ? data.data :
        (data.data && Array.isArray(data.data) ? data.data :
          (Array.isArray(data) ? data : []));
      console.log('[DEBUG] Subjects array to set:', subjectsArray);
      setMonHocList(subjectsArray);
    } catch (err) {
      console.error('[DEBUG] Lỗi fetch /subjects:', err);
      message.error('Không thể tải danh sách môn học');
      setMonHocList([]);
    }
    setLoading(false);
  };

  const fetchLopHocPhan = async (monHocId: string) => {
    if (!monHocId) return;
    setLoading(true);
    try {
      const headers = getAuthHeaders();
      console.log('[DEBUG] Headers for /lophocphan:', headers);

      const res = await fetch(buildAPIURL(`/lophocphan/by-monhoc/${encodeURIComponent(monHocId)}`), { headers });
      const data = await res.json();
      console.log('[DEBUG] API /lophocphan response:', data);

      // Kiểm tra nếu response có format {success: true, data: [...]} hoặc {data: [...]}
      const classSectionsArray = data.success && Array.isArray(data.data) ? data.data :
        (data.data && Array.isArray(data.data) ? data.data :
          (Array.isArray(data) ? data : []));
      console.log('[DEBUG] ClassSections array to set:', classSectionsArray);
      setLopHocPhanList(classSectionsArray);
    } catch (err) {
      console.error('[DEBUG] Lỗi fetch /lophocphan:', err);
      message.error('Không thể tải danh sách lớp học phần');
      setLopHocPhanList([]);
    }
    setLoading(false);
  };

  const handleKhoaHocChange = (value: string) => {
    setSelectedKhoaHoc(value);
    setSelectedMonHoc('');
    setSelectedLopHocPhan('');
    setMonHocList([]);
    setLopHocPhanList([]);
    if (value) {
      fetchMonHoc(value);
    }
  };

  const handleMonHocChange = (value: string) => {
    setSelectedMonHoc(value);
    setSelectedLopHocPhan('');
    setLopHocPhanList([]);
    if (value) {
      fetchLopHocPhan(value);
    }
  };

  return (
    <div style={{ padding: '20px' }}>
      <Card
        title={
          <span>
            <BookOutlined style={{ marginRight: 8 }} />
            Nhập điểm lớp học phần
          </span>
        }
        style={{ marginBottom: 20 }}
      >
        <Row gutter={16}>
          <Col span={8}>
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', marginBottom: 8, fontWeight: 'bold' }}>
                Khóa học:
              </label>
              <Select
                style={{ width: '100%' }}
                placeholder="Chọn khóa học"
                value={selectedKhoaHoc}
                onChange={handleKhoaHocChange}
                loading={loading}
                showSearch
                optionFilterProp="children"
                filterOption={(input, option) =>
                  option?.children?.toString().toLowerCase().includes(input.toLowerCase()) || false
                }
              >
                {khoaHocList.map(khoaHoc => (
                  <Option key={khoaHoc.KhoaHocID} value={khoaHoc.KhoaHocID}>
                    {khoaHoc.TenKhoaHoc}
                  </Option>
                ))}
              </Select>
            </div>
          </Col>

          <Col span={8}>
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', marginBottom: 8, fontWeight: 'bold' }}>
                Môn học:
              </label>
              <Select
                style={{ width: '100%' }}
                placeholder="Chọn môn học"
                value={selectedMonHoc}
                onChange={handleMonHocChange}
                loading={loading}
                disabled={!selectedKhoaHoc}
                showSearch
                optionFilterProp="children"
                filterOption={(input, option) =>
                  option?.children?.toString().toLowerCase().includes(input.toLowerCase()) || false
                }
              >
                {monHocList.map(monHoc => (
                  <Option key={monHoc.MonHocID} value={monHoc.MonHocID}>
                    {monHoc.TenMonHoc}
                  </Option>
                ))}
              </Select>
            </div>
          </Col>

          <Col span={8}>
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', marginBottom: 8, fontWeight: 'bold' }}>
                Lớp học phần:
              </label>
              <Select
                style={{ width: '100%' }}
                placeholder="Chọn lớp học phần"
                value={selectedLopHocPhan}
                onChange={setSelectedLopHocPhan}
                loading={loading}
                disabled={!selectedMonHoc}
                showSearch
                optionFilterProp="children"
                filterOption={(input, option) =>
                  option?.children?.toString().toLowerCase().includes(input.toLowerCase()) || false
                }
              >
                {lopHocPhanList.map(lhp => (
                  <Option key={lhp.LopHocPhanID || lhp.MaLHP} value={lhp.LopHocPhanID || lhp.MaLHP}>
                    {(lhp.LopHocPhanID || lhp.MaLHP)} - {(lhp.TenLopHP || lhp.TenLHP || lhp.TenLopHocPhan || lhp.TenLop)} {lhp.TenGV ? `- ${lhp.TenGV}` : ''}
                  </Option>
                ))}
              </Select>
            </div>
          </Col>
        </Row>
      </Card>

      {selectedLopHocPhan && (
        <>
          <Divider />
          <Spin spinning={loading}>
            <NhapDiemLopHocPhanPage
              lopHocPhanId={selectedLopHocPhan}
              tenKhoaHoc={khoaHocList.find(k => k.KhoaHocID === selectedKhoaHoc)?.TenKhoaHoc}
              tenMonHoc={monHocList.find(m => m.MonHocID === selectedMonHoc)?.TenMonHoc}
              tenLopHocPhan={lopHocPhanList.find(l => (l.LopHocPhanID || l.MaLHP) === selectedLopHocPhan)?.TenLopHP || lopHocPhanList.find(l => (l.LopHocPhanID || l.MaLHP) === selectedLopHocPhan)?.TenLHP || lopHocPhanList.find(l => (l.LopHocPhanID || l.MaLHP) === selectedLopHocPhan)?.TenLop}
              tenGV={currentTeacherName}
            />
          </Spin>
        </>
      )}
    </div>
  );
};

export default GradeInputPage;
